/**
 * `GET tracking/portal/features/` (`portal_views/features.py`): the
 * Django-admin switches Leo ruled off on 2026-10-05, plus `portal_ll33_board`,
 * `portal_boiler_flow` and `portal_yc_demo` (absent on the wire reads OFF), and
 * `portal_judgment_owed` (absent reads ON, Leo 10-31).
 * Never persisted; re-read every 30 s and on tab focus after 30 s, so an admin flip
 * shows within 30 s. Loading, or a first read that failed, reads all OFF; a failed
 * poll keeps the last set. A DRF 404 (tracking access gone) reads OFF; features never 403s.
 * `onTracker` false (Leo Option A, 10-20): the company is not pinned IN on Ingestion Scope; loading reads true.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import api from "@/api/base";
import { httpBodyOf, httpStatusOf } from "./httpStatus";

export interface PortalFeatures {
  actionPlan: boolean;
  buildingRowPanel: boolean;
  hpdExposureLane: boolean;
  ll97Workflow: boolean;
  ll152Workflow: boolean;
  ll33Board: boolean;
  boilerFlow: boolean;
  ycDemo: boolean;
  judgmentOwed: boolean;
  onTracker: boolean;
}

export const PORTAL_FEATURES_OFF: PortalFeatures = {
  actionPlan: false,
  buildingRowPanel: false,
  hpdExposureLane: false,
  ll97Workflow: false,
  ll152Workflow: false,
  ll33Board: false,
  boilerFlow: false,
  ycDemo: false,
  judgmentOwed: false,
  onTracker: true,
};

/** The 403 body off the Tracker (backend tracking/permissions.py NOT_ON_TRACKER); one wording on every surface. */
export const NOT_ON_TRACKER_COPY =
  "This company is not on the Tracker yet. Ask CodeClear to add it.";

export const portalFeaturesQueryKey = ["portal-features"] as const;

type Wire = Partial<Record<keyof PortalFeatures, unknown>>;

const featuresOf = (data: Wire | undefined): PortalFeatures => ({
  actionPlan: data?.actionPlan === true,
  buildingRowPanel: data?.buildingRowPanel === true,
  hpdExposureLane: data?.hpdExposureLane === true,
  ll97Workflow: data?.ll97Workflow === true,
  ll152Workflow: data?.ll152Workflow === true,
  ll33Board: data?.ll33Board === true,
  boilerFlow: data?.boilerFlow === true,
  ycDemo: data?.ycDemo === true,
  judgmentOwed: data?.judgmentOwed !== false,
  onTracker: data?.onTracker !== false,
});

const fetchPortalFeatures = async (): Promise<PortalFeatures> => {
  try {
    const { data } = await api.get<Wire>("tracking/portal/features/");
    return featuresOf(data);
  } catch (e) {
    if (httpStatusOf(e) === 404 && typeof httpBodyOf(e)?.detail === "string")
      return PORTAL_FEATURES_OFF;
    throw e;
  }
};

export const usePortalFeaturesQuery = (enabled = true) => {
  const client = useQueryClient();
  return useQuery({
    queryKey: portalFeaturesQueryKey,
    queryFn: fetchPortalFeatures,
    enabled,
    // 30 s: every mount after the first reads the cache (2 to 3 GETs per load before, 10-31).
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    // One retry on a 5xx, only before any read: a single blip on load never shows Under construction.
    retry: (n, e) =>
      n < 1 &&
      (httpStatusOf(e) ?? 0) >= 500 &&
      client.getQueryData(portalFeaturesQueryKey) === undefined,
    meta: { persist: false },
  });
};

export const usePortalFeatures = (): PortalFeatures => {
  const { data } = usePortalFeaturesQuery();
  const f = data ?? PORTAL_FEATURES_OFF;
  // The row panel reads the Action plan feed, which 404s with that flag off.
  return { ...f, buildingRowPanel: f.buildingRowPanel && f.actionPlan };
};
